import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { IconBuilding, IconUser } from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { getCustomerIndexCached } from '../../services/customerIndexCacheService';
import { updateDomain, type DomainDetail } from '../../services/domainsApi';

// Reusable customer-lookup control for (re)assigning a domain's owning customer.
// Mirrors the AssignOwnerCell pattern in DomainManagerPage: an MUI Autocomplete over
// the cached customer index (dataverseCustomerId), preselected to the current owner,
// + a save button that PATCHes ownerCustomerGuid. The backend handles detaching from
// the previous owner (reassignment-safe).

const optionLabel = (c: CustomerIndexItemDto) => c.businessName || c.displayName;

interface Props {
  domainId: string;
  /** Current owner's Dataverse customer GUID, used to preselect + detect a change. */
  currentOwnerGuid?: string | null;
  /** Called with the re-enriched domain after a successful (re)assignment. */
  onReassigned: (updated: DomainDetail) => void;
  /** Called when the inline editor should close without saving (list usage). */
  onCancel?: () => void;
  /** 32px trigger for dense table rows; 40px (default) for the detail page. */
  dense?: boolean;
}

export const CustomerOwnerSelect: React.FC<Props> = ({
  domainId,
  currentOwnerGuid,
  onReassigned,
  onCancel,
  dense,
}) => {
  const [options, setOptions] = useState<CustomerIndexItemDto[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CustomerIndexItemDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCustomerIndexCached()
      .then((res) => { if (!cancelled) setOptions(res.items); })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load customers');
      });
    return () => { cancelled = true; };
  }, []);

  // Preselect the current owner once the index loads.
  useEffect(() => {
    if (currentOwnerGuid && options.length) {
      setSelected(options.find((o) => o.dataverseCustomerId === currentOwnerGuid) ?? null);
    }
  }, [currentOwnerGuid, options]);

  const changed = !!selected && selected.dataverseCustomerId !== currentOwnerGuid;
  const triggerH = dense ? 32 : 40;

  const save = async () => {
    if (!selected || !changed) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateDomain(domainId, { ownerCustomerGuid: selected.dataverseCustomerId });
      onReassigned(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reassignment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 280 }}>
      <Autocomplete<CustomerIndexItemDto>
        size="small"
        sx={{ flex: 1, minWidth: 200, '& .MuiInputBase-root': { height: triggerH } }}
        options={options}
        value={selected}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(a, b) => a.dataverseCustomerId === b.dataverseCustomerId}
        filterOptions={(opts, state) => {
          const needle = state.inputValue.trim().toLowerCase();
          if (!needle) return opts.slice(0, 30);
          return opts
            .filter((c) =>
              [c.displayName, c.businessName ?? '', c.email ?? '']
                .join(' ')
                .toLowerCase()
                .includes(needle),
            )
            .slice(0, 50);
        }}
        onChange={(_, value) => setSelected(value)}
        disabled={saving || !!loadError}
        renderOption={(props, option) => {
          const icon =
            option.customerType === 'Business' ? (
              <IconBuilding size={16} stroke={1.5} />
            ) : (
              <IconUser size={16} stroke={1.5} />
            );
          return (
            <li {...props} key={option.dataverseCustomerId}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  {icon}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }}>
                    {optionLabel(option)}
                  </Typography>
                  {option.email && (
                    <Typography color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Owner"
            placeholder={loadError ? 'Customers unavailable' : 'Find customer…'}
          />
        )}
        blurOnSelect
        onClick={(e) => e.stopPropagation()}
      />
      <Button
        variant="contained"
        size="small"
        onClick={(e) => { e.stopPropagation(); save(); }}
        disabled={!changed || saving}
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        {currentOwnerGuid ? 'Reassign' : 'Assign'}
      </Button>
      {onCancel && (
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          disabled={saving}
          sx={{ color: '#aaaaaa' }}
        >
          Cancel
        </Button>
      )}
      {(error || loadError) && (
        <Typography variant="caption" color="error" sx={{ maxWidth: 180 }}>
          {error || loadError}
        </Typography>
      )}
    </Box>
  );
};
